import React, { useCallback, useEffect, useMemo, useState } from 'react'
import loadable from '@loadable/component'

import { spacing, useTheme } from '@thg-commerce/enterprise-theme'
import { SvgIcon } from '@thg-commerce/gravity-icons/src'
import { CombinedThemeInterface } from '@thg-commerce/gravity-patterns'
import { withPrefetch } from '@thg-commerce/gravity-system/prefetch'

const Close = loadable(
  () => import('@thg-commerce/gravity-icons/src/components/Close'),
  { ssr: true, fallback: <div style={{ width: 24, height: 24 }} /> },
)

import {
  FlyoutMenuContainer,
  FlyoutMenuContent,
  FlyoutMenuHeader,
  FlyoutMenuOverlay,
  NoLinkContainer,
  PromoButton,
  PromoDescription,
  PromoSection,
  PromoTitle,
  StripBannerSlides,
  StripBannerWrapper,
  StyledHref,
  StyledSafeHtml,
  StyledSVGWrapper,
} from './styles'

export interface StripBannerQueryResult {
  stripBannerTitleOne?: string
  stripBannerContentOne?: string
  stripBannerURLOne?: string
  stripBannerTitleTwo?: string
  stripBannerContentTwo?: string
  stripBannerURLTwo?: string
  stripBannerTitleThree?: string
  stripBannerContentThree?: string
  stripBannerURLThree?: string
  stripBannerTitleFour?: string
  stripBannerContentFour?: string
  stripBannerURLFour?: string
  enableRotatingBanner?: boolean
  hideDefaultBanner?: string
  stripBannerPositionOne?: number
  stripBannerPositionTwo?: number
  stripBannerPositionThree?: number
  stripBannerPositionFour?: number
  stripBannerCTATextOne?: string
  stripBannerCTATextTwo?: string
  stripBannerCTATextThree?: string
  stripBannerCTATextFour?: string
}

export interface StripBannerSlide {
  child: React.ReactNode
  key: string
  order?: number
  link?: string
  icon?: string
  svg?: JSX.Element
  jsx?: JSX.Element
  testId?: string
  useStrokeFill?: boolean
  isClipBoard?: boolean
}

const PrefetchStyledHref = withPrefetch('href', StyledHref)

const Icons = {
  closeIcon: (icon) =>
    icon.svgPath ? (
      <SvgIcon
        xmlns="http://www.w3.org/2000/svg"
        viewBox={icon.viewBox}
        width={icon.width}
        height={icon.height}
      >
        <path d={icon.svgPath} fillRule="evenodd" />
      </SvgIcon>
    ) : (
      <Close />
    ),
}

const createSlideConfig = (
  dataField: string,
  iconType: string | undefined,
  rawPanelInformation: StripBannerQueryResult,
) => {
  const childComponent = (
    <StyledSafeHtml
      data-testid={`${iconType}-text`}
      content={rawPanelInformation[dataField]}
      removeTagsAndContent={false}
      tagsToRemove={[]}
    />
  )

  const urlField = dataField.replace(/^stripBannerTitle/, 'stripBannerURL')
  const link = rawPanelInformation[urlField]

  const orderField = dataField.replace(
    /^stripBannerTitle/,
    'stripBannerPosition',
  )
  const order = rawPanelInformation[orderField]

  return {
    link,
    order,
    child: childComponent,
    key: dataField,
  }
}
export const generateSlides = (
  rawPanelInformation: StripBannerQueryResult | undefined,
  theme: CombinedThemeInterface,
): StripBannerSlide[] => {
  if (!rawPanelInformation) return []

  const slides: StripBannerSlide[] = []

  if (rawPanelInformation.enableRotatingBanner === false) {
    if (rawPanelInformation.stripBannerTitleOne) {
      const panelConfig = createSlideConfig(
        'stripBannerTitleOne',
        undefined,
        rawPanelInformation,
      )
      pushToSlides(slides, panelConfig, theme)
    }
    return slides
  }

  const unsortedSlides: StripBannerSlide[] = []

  Object.keys(rawPanelInformation).forEach((key) => {
    const match = key.match(/^stripBannerTitle(\w+)/)
    if (match && rawPanelInformation[key]) {
      const suffix = match[1]
      const positionKey = `stripBannerPosition${suffix}` as keyof StripBannerQueryResult
      const position = rawPanelInformation[positionKey] ?? 999 // Default high number

      const panelConfig = createSlideConfig(key, undefined, rawPanelInformation)
      panelConfig.order = position
      unsortedSlides.push(panelConfig)
    }
  })

  const sortedSlides = unsortedSlides.sort(
    (a, b) => (a.order ?? 999) - (b.order ?? 999),
  )

  sortedSlides.forEach((slide) => {
    pushToSlides(slides, slide, theme)
  })

  return slides
}

function pushToSlides(
  slides: StripBannerSlide[],
  slide: StripBannerSlide,
  theme: CombinedThemeInterface,
) {
  if (slide.link !== '') {
    slides.push(slide)
  } else {
    delete slide.link
    slides.push(slide)
  }
}

interface FlyoutMenuProps {
  isOpen: boolean
  onClose: () => void
  bannerInfo?: StripBannerQueryResult
}

interface BannerOffer {
  title: string
  content: string
  url: string
  ctaText?: string
  position: number
}

const getOffers = (bannerInfo: StripBannerQueryResult): BannerOffer[] => {
  const offers: BannerOffer[] = []

  const offerKeys = Object.keys(bannerInfo).filter(
    (key) =>
      key.startsWith('stripBannerTitle') &&
      key !== 'stripBannerTitle' &&
      bannerInfo[key],
  )

  offerKeys.forEach((titleKey) => {
    const offerNumber = titleKey.replace('stripBannerTitle', '')
    const contentKey = `stripBannerContent${offerNumber}`
    const urlKey = `stripBannerURL${offerNumber}`
    const ctaKey = `stripBannerCTAText${offerNumber}`
    const positionKey = `stripBannerPosition${offerNumber}`

    offers.push({
      title: bannerInfo[titleKey],
      content: bannerInfo[contentKey],
      url: bannerInfo[urlKey],
      ctaText: bannerInfo[ctaKey],
      position: Number(bannerInfo[positionKey]) || offers.length + 1,
    })
  })

  return offers.sort((a, b) => a.position - b.position)
}

const FlyoutMenu = ({ isOpen, onClose, bannerInfo }: FlyoutMenuProps) => {
  const theme = useTheme()

  const handleClose = () => {
    onClose()
  }

  if (!bannerInfo) {
    return null
  }

  const offers: BannerOffer[] = getOffers(bannerInfo)

  return (
    <FlyoutMenuOverlay className={isOpen ? 'open' : ''} onClick={handleClose}>
      <FlyoutMenuContainer
        className={isOpen ? 'open' : ''}
        onClick={(e) => e.stopPropagation()}
      >
        <FlyoutMenuHeader>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              touchAction: 'manipulation',
            }}
          >
            {Icons.closeIcon(theme.patterns.header.flyout.closeIcon)}
          </button>
        </FlyoutMenuHeader>
        <FlyoutMenuContent>
          {offers.map((offer, index) => (
            <PromoSection key={index}>
              <PromoTitle>{offer.title}</PromoTitle>
              <PromoDescription>{offer.content}</PromoDescription>
              {offer.url && (
                <a href={offer.url} style={{ textDecoration: 'none' }}>
                  <PromoButton>{offer.ctaText}</PromoButton>
                </a>
              )}
            </PromoSection>
          ))}
        </FlyoutMenuContent>
      </FlyoutMenuContainer>
    </FlyoutMenuOverlay>
  )
}
const handleInfoButtonClick = (
  event: React.MouseEvent,
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  event.preventDefault()
  event.stopPropagation()
  setIsModalOpen(true)
}

interface PlusIconProps {
  icon: any
  setIsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>
}

const PlusIcon = ({ icon }: PlusIconProps) => (
  <div
    style={{
      marginRight: spacing(0.5),
      display: 'flex',
      alignContent: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      touchAction: 'manipulation',
    }}
    role={'button'}
  >
    <SvgIcon
      xmlns="http://www.w3.org/2000/svg"
      viewBox={icon.viewBox}
      width={icon.width}
      height={icon.height}
    >
      <path d={icon.svgPath} fill={'white'} fillRule="evenodd" />
    </SvgIcon>
  </div>
)

export const Slides = (props: {
  slides: StripBannerSlide[]
  rawPanelInformation?: StripBannerQueryResult
}) => {
  const { slides, rawPanelInformation } = props
  const theme = useTheme()
  const icons = theme.icons.plus
  const [isModalOpen, setIsModalOpen] = useState(false)

  return (
    <StripBannerWrapper>
      <FlyoutMenu
        data-testid="stripbanner-flyout-menu"
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        bannerInfo={rawPanelInformation}
      />
      <div
        style={{
          position: 'relative',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
        role={'banner'}
        onClick={(e) => handleInfoButtonClick(e, setIsModalOpen)}
      >
        {slides.map((slide, index) => (
          <StripBannerSlides
            data-testid="stripbanner-desktop-slide"
            key={slide.key}
            panelCount={slides.length}
            index={index}
          >
            {slide.link ? (
              <PrefetchStyledHref>
                <StyledSVGWrapper
                  isClipBoard={slide.isClipBoard}
                  useStrokeFill={slide.useStrokeFill}
                  data-testid={slide.testId}
                >
                  {slide.svg}
                </StyledSVGWrapper>
                {slide.child}
                <div style={{ paddingLeft: '20px' }}>
                  <PlusIcon icon={icons} />
                </div>
              </PrefetchStyledHref>
            ) : (
              <NoLinkContainer>{slide.child}</NoLinkContainer>
            )}
          </StripBannerSlides>
        ))}
      </div>
    </StripBannerWrapper>
  )
}
